import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useMarkNotificationAsReadMutation } from '../../services/connectApi';
import { Notification } from '../../models';

interface NotificationDetailsProps {
  open: boolean;
  notification: Notification;
  onClose: () => void;
}

const NotificationDetails: React.FC<NotificationDetailsProps> = ({ open, notification, onClose }) => {
  const [markAsRead] = useMarkNotificationAsReadMutation();

  const handleMarkAsRead = async () => {
    try {
      await markAsRead(notification.id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'primary';
      case 'delivered':
        return 'info';
      case 'read':
        return 'success';
      case 'failed':
        return 'error';
      case 'scheduled':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="notification-details-dialog-title"
    >
      <DialogTitle id="notification-details-dialog-title">
        Notification Details
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">{notification.title}</Typography>
                <Chip 
                  label={notification.status.toUpperCase()} 
                  color={getStatusColor(notification.status)} 
                  size="small" 
                />
              </Box>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Template</Typography>
              <Typography variant="body1">{notification.template_name}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Channel</Typography>
              <Typography variant="body1">{notification.channel_name}</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Body</Typography>
              <Card variant="outlined" sx={{ mt: 1 }}>
                <CardContent>
                  <Typography variant="body1">{notification.body}</Typography>
                </CardContent>
              </Card>
            </Grid>

            {notification.data && Object.keys(notification.data).length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Additional Data</Typography>
                <Card variant="outlined" sx={{ mt: 1 }}>
                  <CardContent>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(notification.data, null, 2)}
                    </pre>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {notification.image_url && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Image</Typography>
                <Box sx={{ mt: 1, maxWidth: 300 }}>
                  <img 
                    src={notification.image_url} 
                    alt="Notification" 
                    style={{ maxWidth: '100%', borderRadius: 8 }} 
                  />
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
                  <Typography variant="body2">
                    {new Date(notification.created_at).toLocaleString()}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Scheduled At</Typography>
                  <Typography variant="body2">
                    {notification.scheduled_at 
                      ? new Date(notification.scheduled_at).toLocaleString() 
                      : 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Sent At</Typography>
                  <Typography variant="body2">
                    {notification.sent_at 
                      ? new Date(notification.sent_at).toLocaleString() 
                      : 'N/A'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Read At</Typography>
                  <Typography variant="body2">
                    {notification.read_at 
                      ? new Date(notification.read_at).toLocaleString() 
                      : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>

            {notification.error_message && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="error">Error Message</Typography>
                <Card variant="outlined" sx={{ mt: 1, bgcolor: '#fff5f5' }}>
                  <CardContent>
                    <Typography variant="body2" color="error">
                      {notification.error_message}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {notification.status === 'delivered' && !notification.read_at && (
          <Button onClick={handleMarkAsRead} color="primary">
            Mark as Read
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NotificationDetails;
